import { useEffect, useState } from 'react';
import type {
  Screen,
  VideoWallDetail,
  VideoWallOperationalState,
  VideoWallTileBlockingReason,
} from '@ghostscreen/contracts';
import { useTranslation } from 'react-i18next';
import { formatDateTime, formatTime } from '../../i18n';
import {
  formatAge,
  manifestLabel,
  tileConnectionColor,
  tileConnectionLabel,
  tileIndex,
  tileReadyLabel,
  wallStatusColor,
  wallStatusLabel,
} from './wallFormat';

type WallHealthCardProps = {
  wall: VideoWallDetail | null;
  screens: Screen[];
};

const BLOCKING_KEYS = {
  no_device: 'noDevice',
  offline: 'offline',
  connection_unknown: 'connectionUnknown',
  not_ready: 'notReady',
  not_playing: 'notPlaying',
} as const satisfies Record<VideoWallTileBlockingReason, string>;

function countColor(count: number, required: number): string {
  if (required === 0) return 'var(--gs-muted)';
  return count === required ? 'var(--gs-ok)' : 'var(--gs-bad)';
}

export function WallHealthCard({ wall, screens }: WallHealthCardProps) {
  const { t } = useTranslation('videoWalls');
  // Re-render so the visible ages keep counting between polls.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 5_000);
    return () => window.clearInterval(timer);
  }, []);

  if (!wall) {
    return (
      <article
        className="overflow-hidden rounded-[14px] border"
        style={{ background: 'var(--gs-panel)', borderColor: 'var(--gs-line)' }}
      >
        <div className="border-b px-[22px] py-[17px]" style={{ borderColor: 'var(--gs-line)' }}>
          <h3 className="m-0 text-base font-bold">{t('health.title')}</h3>
        </div>
        <p className="m-0 px-[22px] py-4 text-sm" style={{ color: 'var(--gs-muted)' }}>
          {t('health.selectPrompt')}
        </p>
      </article>
    );
  }

  const ops = wall.operations;
  const publication = wall.activePublication;
  const tiles = [...wall.tiles].sort(
    (a, b) => tileIndex(a.row, a.col, wall.cols) - tileIndex(b.row, b.col, wall.cols),
  );
  const hasReadyGate = publication != null && ops.state !== 'stopped' && ops.state !== 'failed';

  const facts = [
    {
      label: t('health.facts.tilesOnline'),
      value: t('health.facts.count', { value: ops.onlineTileCount, total: ops.requiredTileCount }),
      color: countColor(ops.onlineTileCount, ops.requiredTileCount),
    },
    {
      label: t('health.facts.tilesReady'),
      value: hasReadyGate
        ? t('health.facts.count', { value: ops.liveReadyTileCount, total: ops.requiredTileCount })
        : '—',
      color: hasReadyGate
        ? countColor(ops.liveReadyTileCount, ops.requiredTileCount)
        : 'var(--gs-muted)',
    },
    {
      label: t('health.facts.manifest'),
      value: manifestLabel(publication),
      color: 'var(--gs-ink)',
    },
    {
      label: t('health.facts.timeline'),
      value: publication ? publication.timelineId.slice(0, 10) : '—',
      color: 'var(--gs-ink)',
    },
    {
      label: ops.state === 'starting' ? t('health.facts.starts') : t('health.facts.started'),
      value:
        publication?.epochStartAt &&
        (ops.state === 'starting' || ops.state === 'on_air' || ops.state === 'interrupted')
          ? formatTime(publication.epochStartAt)
          : '—',
      color: 'var(--gs-ink)',
    },
  ];

  return (
    <article
      className="overflow-hidden rounded-[14px] border"
      style={{ background: 'var(--gs-panel)', borderColor: 'var(--gs-line)' }}
    >
      <div
        className="flex flex-wrap items-center gap-2.5 border-b px-[22px] py-[17px]"
        style={{ borderColor: 'var(--gs-line)' }}
      >
        <h3 className="m-0 text-base font-bold">{t('health.title')}</h3>
        <span
          className="ms-auto text-[11.5px] font-bold uppercase tracking-[0.1em]"
          style={{ color: wallStatusColor(ops.state) }}
        >
          {/* The server state itself (no separate "Excellent"-style grade); counts are listed below. */}
          {wallStatusLabel(ops.state, wall.mode)}
        </span>
      </div>

      <div
        className="flex flex-wrap gap-x-[26px] gap-y-[18px] border-b px-[22px] py-4"
        style={{ borderColor: 'var(--gs-line)' }}
      >
        {facts.map((fact) => (
          <span key={fact.label} className="flex flex-col gap-1">
            <span
              className="text-[11px] font-bold uppercase tracking-[0.14em]"
              style={{ color: 'var(--gs-muted)' }}
            >
              {fact.label}
            </span>
            <span className="text-[14.5px] font-bold" style={{ color: fact.color }}>
              {fact.value}
            </span>
          </span>
        ))}
        <span className="basis-full text-xs" style={{ color: 'var(--gs-muted)' }}>
          {t('health.driftNote', { age: formatAge(ops.evaluatedAt, now) })}
        </span>
      </div>

      {tiles.length === 0 ? (
        <p className="m-0 px-[22px] py-4 text-sm" style={{ color: 'var(--gs-muted)' }}>
          {t('health.noTiles')}
        </p>
      ) : (
        tiles.map((tile) => {
          const screen = screens.find((item) => item.id === tile.screenId);
          const index = tileIndex(tile.row, tile.col, wall.cols);
          const leader = tile.role === 'leader';
          const notes = [
            tile.blockingReason ? t(`health.blocking.${BLOCKING_KEYS[tile.blockingReason]}`) : null,
            tile.stuck && tile.waitingForFrameSeconds !== null
              ? t('health.stuck', {
                  tile: t('tile.name', { index }),
                  seconds: tile.waitingForFrameSeconds,
                })
              : null,
            tile.multipleDevicesLinked ? t('health.multipleDevices') : null,
          ].filter((note): note is string => note !== null);
          return (
            <div
              key={`${tile.row}-${tile.col}`}
              className="flex items-start gap-3 border-b px-[22px] py-3 last:border-b-0"
              style={{ borderColor: 'var(--gs-line)' }}
              title={
                tile.lastHeartbeatAt
                  ? t('health.lastHeartbeat', {
                      time: formatDateTime(tile.lastHeartbeatAt, {
                        dateStyle: 'short',
                        timeStyle: 'medium',
                      }),
                    })
                  : t('health.noHeartbeatRecorded')
              }
            >
              <span
                className="mt-[6px] h-[7px] w-[7px] shrink-0 rounded-full"
                style={{ background: tileConnectionColor(tile.connection) }}
                aria-label={tileConnectionLabel(tile.connection)}
              />
              {/* Name, then the status labels on their own line, then device and heartbeat:
                  each gets the full card width and wraps only at spaces. */}
              <span className="flex min-w-0 flex-1 flex-col gap-1">
                <span className="text-[13.5px] font-semibold">
                  {screen?.name ?? t('tile.name', { index })}
                </span>
                <span className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] font-bold uppercase tracking-[0.1em]">
                  <span style={{ color: tileConnectionColor(tile.connection) }}>
                    {tileConnectionLabel(tile.connection)}
                  </span>
                  {hasReadyGate ? (
                    <span
                      style={{
                        color:
                          tileReadyLabel(tile.ready, tile.connection) === 'ready'
                            ? 'var(--gs-ok)'
                            : 'var(--gs-muted)',
                      }}
                    >
                      {tileReadyLabel(tile.ready, tile.connection) === 'ready'
                        ? t('health.tileReady')
                        : tileReadyLabel(tile.ready, tile.connection) === 'readyLastReported'
                          ? t('health.tileReadyLastReported')
                          : t('health.tileWaiting')}
                    </span>
                  ) : null}
                  {hasReadyGate && tile.playing ? (
                    <span style={{ color: 'var(--gs-ok)' }}>{t('health.tilePlaying')}</span>
                  ) : null}
                  <span style={{ color: leader ? 'var(--gs-accent)' : 'var(--gs-muted)' }}>
                    {leader
                      ? t('health.role.leader')
                      : tile.muted
                        ? t('health.role.followerMuted')
                        : t('health.role.follower')}
                  </span>
                </span>
                <span className="font-mono text-[11.5px]" style={{ color: 'var(--gs-muted)' }}>
                  {t('health.tileMeta', {
                    device: tile.deviceId
                      ? t('health.device', { id: tile.deviceId.slice(0, 8) })
                      : t('health.noDevice'),
                    heartbeat: tile.lastHeartbeatAt
                      ? t('health.heartbeat', { age: formatAge(tile.lastHeartbeatAt, now) })
                      : t('health.noHeartbeat'),
                  })}
                </span>
                {notes.map((note) => (
                  <span key={note} className="text-xs" style={{ color: 'var(--gs-bad)' }}>
                    {note}
                  </span>
                ))}
              </span>
            </div>
          );
        })
      )}
    </article>
  );
}
